import type { ApiErrorBody, ErrorCode } from '@codepass/shared';

/**
 * API-klient.
 *
 * `credentials: 'include'` er ikke til forhandling: sessionen ligger i HTTP-only cookies,
 * som browseren kun sender med hvis vi beder om det.
 *
 * Ved 401 forsoeges én refresh, og kaldet gentages. Samtidige 401'er deler det samme
 * refresh-kald - ellers ville tolv parallelle forespoergsler udloese tolv refresh-kald,
 * og Supabases token-rotation ville invalidere hinandens tokens.
 */

/**
 * Relativ som standard, praecis som den vaerdi der saettes i produktion.
 *
 * API'et ligger paa samme origin i baade udvikling (Vites proxy, se vite.config.ts) og
 * produktion (nginx i web-containeren), saa `/v1` er rigtigt i begge. Fallbacken var
 * tidligere den absolutte `http://localhost:5173/v1`, og det var netop derfor et manglende
 * VITE_API_URL kunne vaere ubemaerket i udvikling og forkert i produktion.
 */
const BASE_URL = (import.meta.env['VITE_API_URL'] as string | undefined) ?? '/v1';

export class ApiError extends Error {
  constructor(
    readonly code: ErrorCode,
    message: string,
    readonly status: number,
    readonly details?: Record<string, unknown>,
    readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

type Listener = () => void;
const unauthorizedListeners = new Set<Listener>();

/** Kaldes naar en refresh endeligt fejler, saa AuthProvider kan rydde sin tilstand. */
export function onUnauthorized(listener: Listener): () => void {
  unauthorizedListeners.add(listener);
  return () => unauthorizedListeners.delete(listener);
}

type ForbiddenListener = (error: ApiError, path: string) => void;
const forbiddenListeners = new Set<ForbiddenListener>();

/**
 * Kaldes ved enhver 403.
 *
 * En 403 betyder at serverens billede af brugeren ikke er det samme som klientens:
 * enten er rollen aendret, eller ogsaa er kontoen lukket. Begge dele skal rette UI'et,
 * og stien sendes med saa lytteren kan undgaa at hente det den lige fik afvist.
 */
export function onForbidden(listener: ForbiddenListener): () => void {
  forbiddenListeners.add(listener);
  return () => forbiddenListeners.delete(listener);
}

/**
 * Endpoints hvor en 401 ikke maa udloese refresh-og-proev-igen.
 *
 * `/auth/refresh` er selve loekkevaernet: en refresh der selv svarer 401 maa ikke starte
 * en ny. `/auth/login` svarer 401 ved forkert adgangskode - et retry ville sende de samme
 * forkerte credentials igen og fordoble antallet af fejlede forsoeg i audit_log.
 * `/auth/logout` og `/auth/password-reset` svarer altid ok og har intet at genforsoege.
 *
 * `/auth/me` staar bevidst IKKE her. Dens 401 betyder praecis "sessionen er udloebet",
 * som er det tilfaelde refresh findes for. Tidligere udelukkede guarden hele `/auth/*`, og
 * en bruger med gyldigt refresh-token blev derfor sendt til login hver gang access-tokenet
 * var over en time gammelt.
 */
const NO_REFRESH_RETRY = new Set([
  '/auth/login',
  // Byttet af OAuth-tokenet til en cookie. Der er ingen session at forny endnu, og en
  // 401 herfra betyder at tokenet eller profilen er afvist - ikke at noget er udloebet.
  '/auth/session',
  '/auth/refresh',
  '/auth/logout',
  '/auth/password-reset',
]);

/** buildUrl accepterer sti baade med og uden foranstillet skraastreg - normaliser foer opslag. */
function allowsRefreshRetry(path: string): boolean {
  return !NO_REFRESH_RETRY.has(path.startsWith('/') ? path : `/${path}`);
}

let refreshInFlight: Promise<boolean> | null = null;

async function refreshSession(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    try {
      const response = await fetch(`${BASE_URL}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      return response.ok;
    } catch {
      return false;
    } finally {
      // Nulstilles i mikrotask-koeen, saa alle ventende kald ser samme resultat.
      queueMicrotask(() => {
        refreshInFlight = null;
      });
    }
  })();

  return refreshInFlight;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  /** Intern: forhindrer uendelig refresh-loekke. */
  retried?: boolean;
  signal?: AbortSignal;
  headers?: Record<string, string>;
  /**
   * Kaldet har ingen session og skal ikke have en - TV-dashboardet. En 401 udloeser da ingen
   * refresh, og hverken 401 eller 403 meldes til AuthProvider: der er ingen bruger at rydde, og
   * en afvisning her siger intet om nogens rolle.
   */
  sessionless?: boolean;
}

/**
 * `window.location.origin` som base er ikke valgfri.
 *
 * BASE_URL er `/v1` i produktion - relativ, fordi API'et ligger paa samme origin og
 * nginx i web-containeren proxier stien videre (se apps/web/Dockerfile). `new URL()` med
 * ét argument kraever en absolut URL og kaster ellers
 *   TypeError: Failed to construct 'URL': Invalid URL
 * paa hvert enkelt API-kald. Med en base oploeses den relative sti mod den side, brugeren
 * staar paa; er BASE_URL absolut, bliver basen ignoreret, saa begge former virker.
 *
 * Fejlen kunne ikke ses i udvikling, hvor fallbacken nedenfor var absolut. Foerste kald i
 * produktion er /auth/session i OAuth-callbacket, saa den viste sig som et login der
 * faldt - ikke som en klient der var gaaet i staa overalt.
 */
function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = new URL(
    `${BASE_URL}${path.startsWith('/') ? path : `/${path}`}`,
    window.location.origin,
  );
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null || value === '') continue;
      url.searchParams.set(key, String(value));
    }
  }
  return url.toString();
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';

  const response = await fetch(buildUrl(path, options.query), {
    method,
    credentials: 'include',
    headers: {
      ...(options.body === undefined ? {} : { 'Content-Type': 'application/json' }),
      ...options.headers,
    },
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
    signal: options.signal ?? null,
  });

  if (
    response.status === 401 &&
    !options.retried &&
    !options.sessionless &&
    allowsRefreshRetry(path)
  ) {
    const refreshed = await refreshSession();
    if (refreshed) {
      return apiRequest<T>(path, { ...options, retried: true });
    }
    for (const listener of unauthorizedListeners) listener();
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload: unknown = text.length > 0 ? safeJsonParse(text) : null;

  if (!response.ok) {
    const body = payload as ApiErrorBody | null;
    const error = new ApiError(
      body?.error?.code ?? 'INTERNAL_ERROR',
      body?.error?.message ?? 'Der opstod en uventet fejl.',
      response.status,
      body?.error?.details,
      body?.request_id,
    );

    // Notificér foer der kastes: kalderen haandterer typisk fejlen lokalt, og saa ville
    // en aendret rolle eller en lukket konto aldrig naa frem til AuthProvider.
    if (response.status === 403 && !options.sessionless) {
      for (const listener of forbiddenListeners) listener(error, path);
    }

    throw error;
  }

  return payload as T;
}

function safeJsonParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return { raw: text };
  }
}

export const api = {
  get: <T>(path: string, query?: RequestOptions['query']) => apiRequest<T>(path, { query }),
  post: <T>(path: string, body?: unknown) => apiRequest<T>(path, { method: 'POST', body }),
  put: <T>(path: string, body?: unknown) => apiRequest<T>(path, { method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown) => apiRequest<T>(path, { method: 'PATCH', body }),
  /** `del` og ikke `delete`: det sidste er et reserveret ord som metodenavn i ældre bundlere. */
  del: <T>(path: string) => apiRequest<T>(path, { method: 'DELETE' }),
};

/** Absolut URL - bruges til CSV-download, hvor browseren skal navigere selv. */
export function apiUrl(path: string, query?: RequestOptions['query']): string {
  return buildUrl(path, query);
}
